import { useFeatureGate } from '@statsig/react-bindings';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useContext, useEffect, useRef } from 'react';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { TelehealthSessionInfoData_TelehealthSessionInfo_Appointment } from '../../hooks';
import { StatsigLoadingContext } from '../../statsig/StatsigProvider';
import {
  telehealth4MinMeasureRestrictionDisabledGate,
  telehealthAutoOpenIncompleteMeasuresDrawer,
  telehealthAutoOpenReflectionsGate,
} from '../../statsig/gates';
import { useGetClientSafetyGuard } from '../../useGetClientSafetyGuard';
import { useGetShouldShowReflectionTopics } from '../reflections/useGetShouldShowReflectionTopics';
import { VisitorDrawerState, visitorMeasuresAtom } from '../state';
import {
  PreSessionPromptWrapper as PreSessionPrompt,
  PreSessionPromptVariant,
} from './pre-session-prompt/PreSessionPrompt';
import { usePreSessionPromptState } from './pre-session-prompt/usePreSessionPromptState';

const PRE_SESSION_PROMPT_THRESHOLD_MINUTES = 4;

type SessionInfoSectionWrapperProps = {
  appointment?: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
  openDrawer: (state: VisitorDrawerState) => void;
};

function isWithinThreshold(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  const isWithinThreshold =
    differenceInMinutes(parseISO(appointmentStartTime), new Date()) > PRE_SESSION_PROMPT_THRESHOLD_MINUTES;
  return isWithinThreshold;
}

function appointmentHasStarted(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  return Date.now() > new Date(appointmentStartTime).getTime();
}
export function SessionInfoSectionWrapper({ appointment, ...props }: SessionInfoSectionWrapperProps) {
  const { shouldShowReflectionTopics, isLoading: isLoadingShouldShowReflectionTopics } =
    useGetShouldShowReflectionTopics();
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const hasForms = !!visitorMeasures?.length;
  const formsAreLoading = visitorMeasures === undefined;
  const { loading: isLoadingSafetyGuardStatus, requiresSafetyGuards } = useGetClientSafetyGuard();
  const isLoading =
    useContext(StatsigLoadingContext) ||
    isLoadingShouldShowReflectionTopics ||
    formsAreLoading ||
    isLoadingSafetyGuardStatus;
  const { preSessionPrompt, setPreSessionPrompt } = usePreSessionPromptState();
  const hasClearedLocalStorageRef = useRef(false); // to prevent re-computation
  const { value: is4MinRestrictionDisabled } = useFeatureGate(telehealth4MinMeasureRestrictionDisabledGate);
  const { value: isAutoOpenMeasuresDrawerEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);
  const { value: isAutoOpenReflectionsEnabled } = useFeatureGate(telehealthAutoOpenReflectionsGate);

  const measures = visitorMeasures ?? [];
  const visitorMeasuresAreAllScreeners =
    measures.length > 0 &&
    measures.reduce(
      (prev, currMeasure) =>
        prev &&
        !!currMeasure &&
        (currMeasure.schema.type === MeasureSchemaType.GAD7_WITH_SCREENER ||
          currMeasure.schema.type === MeasureSchemaType.PHQ9_WITH_SCREENER),
      true,
    );

  useEffect(
    function setPreSessionPromptState() {
      if (hasClearedLocalStorageRef.current) return;
      if (isLoading) return;
      const hasAppointment = !!appointment?.timeStart;

      const shouldShowScreener = !appointmentHasStarted(appointment?.timeStart) && visitorMeasuresAreAllScreeners;

      const shouldShowClientMeasures =
        !isAutoOpenMeasuresDrawerEnabled &&
        (is4MinRestrictionDisabled ||
          (hasAppointment && isWithinThreshold(appointment?.timeStart)) ||
          shouldShowScreener) &&
        hasForms &&
        requiresSafetyGuards === false;

      // Priority order:
      // 1. Measures CTA (only when auto-open drawer gate is disabled)
      // 2. Reflection Topics (if the user is authenticated and the user has
      //    reflection topics, and the auto-open reflections gate is disabled —
      //    when the gate is on, the drawer auto-opens to reflections so the CTA
      //    is redundant and we fall through to Vibes instead)
      // 3. Vibes (if no other pre-session prompt applies). This is the only
      //    option for clients who require safety guards, who are not eligible
      //    to fill out any forms.

      if (preSessionPrompt !== undefined) {
        // once set, always set
        return;
      } else if (shouldShowClientMeasures) {
        setPreSessionPrompt(PreSessionPromptVariant.CLIENT_MEASURES);
      } else if (
        shouldShowReflectionTopics &&
        hasAppointment &&
        requiresSafetyGuards === false &&
        !isAutoOpenReflectionsEnabled
      ) {
        setPreSessionPrompt(PreSessionPromptVariant.REFLECTION_TOPICS);
      } else {
        setPreSessionPrompt(PreSessionPromptVariant.VIBES);
      }
    },
    [
      isLoading,
      isAutoOpenMeasuresDrawerEnabled,
      isAutoOpenReflectionsEnabled,
      is4MinRestrictionDisabled,
      appointment?.timeStart,
      hasForms,
      preSessionPrompt,
      requiresSafetyGuards,
      setPreSessionPrompt,
      shouldShowReflectionTopics,
      visitorMeasuresAreAllScreeners,
    ],
  );

  return (
    <div className='flex flex-col items-center gap-y-2 p-4'>
      <PreSessionPrompt isLoading={isLoading} variant={preSessionPrompt} {...props} />
    </div>
  );
}
